// Web Audio synth: warm additive tones, no external audio files.
//
// iOS/Safari hardening (audio must work on real iPhones, not just desktop):
//  1. The AudioContext is created AND resumed inside genuine user gestures
//     (pointerdown / touchend listeners + every play call re-checks state).
//  2. navigator.audioSession.type = "playback" (Safari 17+) so the hardware
//     mute switch does NOT silence us (Web Audio defaults to "ambient").
//  3. A looping silent (non-muted) <audio> element nudges older iOS versions
//     out of the ambient category for the same reason.
//  4. Every playback re-verifies ctx.state and resumes if needed; a watchdog
//     confirms ctx.currentTime actually advances, else we surface an error.
//  5. Every play creates FRESH oscillator nodes — sources are single-use.
//  6. Nothing autoplays: all sound starts from a tap/click handler.
let ctx = null;
let sessionEl = null;

const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQAAAAA=";

function logErr(where, e) {
  console.error(`[ChoirQuest audio] ${where}:`, e);
}

export async function audioCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) throw new Error("no-audio");
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch (e) {
      logErr("resume", e);
    }
  }
  if (ctx.state !== "running") throw new Error("audio-blocked");
  return ctx;
}

// iOS: move the audio session out of "ambient" (which the mute switch silences)
// and keep the context alive across tab switches.
function activateIOSSession() {
  try {
    if (navigator.audioSession && navigator.audioSession.type !== "playback") {
      navigator.audioSession.type = "playback";
    }
  } catch (e) {
    logErr("audioSession", e);
  }
  try {
    if (!sessionEl) {
      sessionEl = new Audio(SILENT_WAV);
      sessionEl.loop = true;
      sessionEl.playsInline = true;
      sessionEl.setAttribute("playsinline", "");
    }
    sessionEl.play().then(() => {
      setTimeout(() => { try { sessionEl.pause(); } catch (e) { logErr("session pause", e); } }, 3000);
    }).catch((e) => logErr("session element", e));
  } catch (e) {
    logErr("session setup", e);
  }
}

// Shared output bus + analyser: detects "running but silent" contexts (macOS
// output-device / sample-rate changes can leave a context clocking with zero
// output). playAudioSpec probes the bus and self-heals by rebuilding the context.
let bus = null;
let probe = null;
function outBus(c) {
  if (!bus || bus.context !== c) {
    bus = c.createGain();
    probe = c.createAnalyser();
    probe.fftSize = 512;
    bus.connect(probe);
    probe.connect(c.destination);
  }
  return bus;
}

async function assertOutput() {
  if (!probe) return;
  const data = new Float32Array(probe.fftSize);
  for (let i = 0; i < 6; i++) {
    await new Promise((r) => setTimeout(r, 90));
    probe.getFloatTimeDomainData(data);
    for (let j = 0; j < data.length; j++) {
      if (Math.abs(data[j]) > 0.0005) return;
    }
  }
  throw new Error("audio-silent");
}

// iOS/Safari: unlock audio on the very first touch/click anywhere.
let unlockInstalled = false;
export function installAudioUnlock() {
  if (unlockInstalled) return;
  unlockInstalled = true;
  const unlock = async () => {
    activateIOSSession();
    try {
      const c = await audioCtx();
      const buf = c.createBuffer(1, 1, 22050);
      const src = c.createBufferSource();
      src.buffer = buf;
      src.connect(c.destination);
      src.start(0);
    } catch (e) {
      logErr("unlock failed", e);
    }
  };
  ["pointerdown", "touchend", "click"].forEach((ev) =>
    window.addEventListener(ev, unlock, { passive: true }));
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && ctx && ctx.state === "suspended") {
      ctx.resume().catch((e) => logErr("resume on visibility", e));
    }
  });
}

export const midiToFreq = (m) => 440 * Math.pow(2, (m - 69) / 12);

const KEY_TO_SEMITONE = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };

export function vexKeyToMidi(key) {
  const [name, oct] = key.split("/");
  const letter = name[0].toLowerCase();
  // accidentals live AFTER the letter: "b/4" is B natural; "bb/4" is B-flat.
  // (The old `name.includes("b")` check turned every B natural into B-flat.)
  const acc = name.slice(1);
  const accidental = acc.startsWith("#") ? 1 : acc.startsWith("b") ? -1 : 0;
  return 12 * (parseInt(oct, 10) + 1) + KEY_TO_SEMITONE[letter] + accidental;
}

// Dotted durations may be encoded in the string ("qd") or via a dot flag.
const DUR_BEATS = { w: 4, h: 2, q: 1, 8: 0.5, 16: 0.25 };
function durBeats(dur, dot) {
  let d = dur.replace(/r$/, "");
  let dotted = !!dot;
  if (d.endsWith("d")) { dotted = true; d = d.slice(0, -1); }
  const b = DUR_BEATS[d] ?? 1;
  return dotted ? b * 1.5 : b;
}

// After starting playback, confirm the clock is really advancing (iOS can
// silently stay suspended). Retries a resume once before throwing, so callers
// only show the visible error when sound genuinely cannot start.
async function assertAudible(c) {
  for (let attempt = 0; attempt < 2; attempt++) {
    const t = c.currentTime;
    await new Promise((r) => setTimeout(r, 350));
    if (c.state === "running" && c.currentTime > t) return;
    try { await c.resume(); } catch { /* retry loop handles it */ }
  }
  if (c.state !== "running") throw new Error("audio-stalled");
}

async function tone(midi, { duration = 0.9, when = 0, gain = 0.22, sweepTo = null } = {}) {
  const c = await audioCtx();
  const t0 = c.currentTime + when;
  const master = c.createGain();
  if (sweepTo !== null) {
    // true dynamic journey: start AT gain, travel to sweepTo, then release
    master.gain.setValueAtTime(gain, t0);
    master.gain.linearRampToValueAtTime(sweepTo, t0 + duration * 0.85);
    master.gain.linearRampToValueAtTime(0.0001, t0 + duration);
  } else {
    master.gain.setValueAtTime(0, t0);
    master.gain.linearRampToValueAtTime(gain, t0 + 0.06);
    master.gain.setValueAtTime(gain, t0 + Math.max(0.1, duration * 0.88));
    master.gain.linearRampToValueAtTime(0.0001, t0 + duration);
  }
  master.connect(outBus(c));
  [[1, 1], [2, 0.45], [3, 0.22], [4, 0.12]].forEach(([mult, amp]) => {
    const osc = c.createOscillator();
    osc.type = "sine";
    osc.frequency.value = midiToFreq(midi) * mult;
    const g = c.createGain();
    g.gain.value = amp;
    osc.connect(g).connect(master);
    osc.start(t0);
    osc.stop(t0 + duration + 0.05);
  });
}

async function chord(midis, { duration = 1.5, when = 0, gain = 0.14 } = {}) {
  await Promise.all(midis.map((m, i) => tone(m, { duration, when: when + i * 0.02, gain })));
}

// Pitched tick (woodblock-like): the accent level drives pitch AND loudness, so
// metre is genuinely audible as STRONG-weak-medium-weak — not identical clicks.
const TICK_FREQ = { S: 1319, m: 988, w: 659, s: 494 };
const TICK_GAIN = { S: 0.32, m: 0.22, w: 0.14, s: 0.08 };
async function tick(when, level = "w") {
  const c = await audioCtx();
  const t0 = c.currentTime + when;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "triangle";
  osc.frequency.value = TICK_FREQ[level] ?? TICK_FREQ.w;
  g.gain.setValueAtTime(TICK_GAIN[level] ?? TICK_GAIN.w, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.12);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.13);
}

async function knock(when, strong = false) {
  await tick(when, strong ? "S" : "w");
}

async function playRhythm(pattern, beatSecs = 0.55) {
  let t = 0;
  for (const beats of pattern) {
    await knock(t, true);
    t += beats * beatSecs;
  }
  await new Promise((r) => setTimeout(r, t * 1000));
}

async function playMidiSequence(notes, offset = 0, noteDur = 0.5) {
  for (let i = 0; i < notes.length; i++) {
    await tone(notes[i], { duration: noteDur * 0.95, when: offset + i * noteDur });
  }
  await new Promise((r) => setTimeout(r, (offset + notes.length * noteDur) * 1000));
}

// One canonical event list -> sound. The notation on screen is derived from the
// same event list (or IS the spec for melody_vex), so they can never disagree.
const SPEC_PLAYERS = {
  interval: async (s) => {
    const base = s.base ?? 60;
    if (s.mode === "harmonic") {
      await tone(base, { duration: 1.5 });
      await tone(base + (s.semitones ?? 7), { duration: 1.5, gain: 0.18 });
    } else {
      await tone(base, { duration: 0.8 });
      await tone(base + (s.semitones ?? 7), { duration: 1.1, when: 0.9 });
    }
  },
  chord: async (s) => {
    const base = s.base ?? 60;
    const third = s.quality === "minor" ? 3 : 4;
    await chord([base, base + third, base + 7]);
  },
  metre: async (s) => {
    const beats = s.beats || 3;
    const bars = s.bars || 2;
    const accents = s.accents || (beats === 4 ? ["S", "w", "m", "w"] : beats === 3 ? ["S", "w", "w"] : ["S", "w"]);
    const subdiv = s.subdiv || 1;
    for (let bar = 0; bar < bars; bar++) {
      for (let b = 0; b < beats; b++) {
        const t = (bar * beats + b) * 0.55;
        await tick(t, accents[b % accents.length]);
        for (let k = 1; k < subdiv; k++) await tick(t + (k * 0.55) / subdiv, "s");
      }
    }
    await new Promise((r) => setTimeout(r, beats * bars * 550));
  },
  // note <-> rest equivalence: a sustained note, then silence of the SAME length
  // with a quiet pulse ticking through it so the rest can be counted aloud.
  note_rest_pulse: async (s) => {
    const beat = 0.55;
    const beats = s.beats ?? 4;
    const reps = s.repeats ?? 2;
    for (let r = 0; r < reps; r++) {
      const t0 = r * beats * 2 * beat;
      await tone(s.midi ?? 64, { duration: beats * beat * 0.97, when: t0, gain: 0.22 });
      for (let b = 0; b < beats; b++) await tick(t0 + (beats + b) * beat, b === 0 ? "m" : "s");
    }
    await new Promise((r) => setTimeout(r, reps * beats * 2 * beat * 1000));
  },
  rhythm: async (s) => playRhythm(s.pattern || [1, 1, 1, 1]),
  rhythm_compare: async (s) => {
    await playRhythm(s.p1 || []);
    const p1Dur = (s.p1 || []).reduce((a, b) => a + b, 0) * 0.55;
    let t = p1Dur + 0.9;
    for (const beats of s.p2 || []) {
      await knock(t, true);
      t += beats * 0.55;
    }
  },
  melody: async (s) => playMidiSequence(s.midi || [], 0, 0.55),
  // melody_vex with optional 4-beat click count-in and per-note gain
  melody_vex: async (s) => {
    const beatSecs = 0.55;
    if (s.count_in) {
      for (let i = 0; i < s.count_in; i++) await knock(i * beatSecs, i === s.count_in - 1);
    }
    let t = (s.count_in || 0) * beatSecs;
    for (const n of s.notes || []) {
      const beats = durBeats(n.dur, n.dot);
      if (!n.dur.endsWith("r")) {
        const midis = (n.keys || []).map(vexKeyToMidi);
        if (midis.length === 1) {
          await tone(midis[0], { duration: beats * beatSecs * 0.95, when: t });
        } else if (midis.length > 1) {
          await chord(midis, { duration: beats * beatSecs * 0.95, when: t });
        }
      }
      t += beats * beatSecs;
    }
    await new Promise((r) => setTimeout(r, t * 1000));
  },
  melody_compare: async (s) => {
    await playMidiSequence(s.notes1 || [], 0, 0.5);
    await playMidiSequence(s.notes2 || [], (s.notes1 || []).length * 0.5 + 0.9, 0.5);
  },
  dynamics: async (s) => {
    const cresc = s.dir === "crescendo";
    await tone(60, { duration: 2.2, gain: cresc ? 0.06 : 0.26, sweepTo: cresc ? 0.28 : 0.05 });
  },
  // multiple simultaneous voices (canon, two-part hymn, ensemble texture).
  // parts: [{notes: vexNotes, delay_beats?: n, gain?: multiplier}]
  parts: async (s) => {
    const beatSecs = s.beat_secs || 0.55;
    for (const p of s.parts || []) {
      let t = 0.12 + (p.delay_beats || 0) * beatSecs;
      for (const n of p.notes || []) {
        const beats = durBeats(n.dur, n.dot);
        if (!n.dur.endsWith("r")) {
          const midis = (n.keys || []).map(vexKeyToMidi);
          const g = (n.gain ?? 0.2) * (p.gain ?? 1);
          if (midis.length === 1) {
            await tone(midis[0], { duration: beats * beatSecs * 0.95, when: t, gain: g });
          } else if (midis.length > 1) {
            await chord(midis, { duration: beats * beatSecs * 0.95, when: t, gain: 0.13 * (p.gain ?? 1) });
          }
        }
        t += beats * beatSecs;
      }
    }
  },
  // a real phrase with a true dynamic journey: per-note gain interpolated from->to,
  // or an explicit per-note `gain` (accents / sforzando are genuinely audible).
  dyn_melody: async (s) => {
    const beatSecs = s.beat_secs || 0.6;
    const sounding = (s.notes || []).filter((n) => !n.dur.endsWith("r"));
    let t = 0.12;
    let i = 0;
    for (const n of s.notes || []) {
      const beats = durBeats(n.dur, n.dot);
      if (!n.dur.endsWith("r")) {
        const frac = sounding.length > 1 ? i / (sounding.length - 1) : 0;
        const g = n.gain ?? ((s.from ?? 0.08) + ((s.to ?? 0.26) - (s.from ?? 0.08)) * frac);
        const midis = (n.keys || []).map(vexKeyToMidi);
        if (midis.length === 1) await tone(midis[0], { duration: beats * beatSecs * 0.95, when: t, gain: g });
        else if (midis.length > 1) await chord(midis, { duration: beats * beatSecs * 0.95, when: t, gain: g * 0.6 });
        i++;
      }
      t += beats * beatSecs;
    }
  },
  tempo_compare: async (s) => {
    const pat = [1, 1, 0.5, 0.5, 1, 1];
    let t = 0;
    for (const b of pat) { await knock(t, true); t += b * 0.5; }
    t += 0.9;
    const ratio = s.ratio || 0.7;
    for (const b of pat) { await knock(t, true); t += b * 0.5 * ratio; }
  },
  cadence: async (s) => {
    const [c1, c2] = s.chords || [[55, 59, 62], [48, 52, 55]];
    await chord(c1, { duration: 1.3 });
    await chord(c2, { duration: 1.8, when: 1.6 });
  },
  // cantor cue -> silence for the learner to sing -> confirmation of the correct note
  cue_note: async (s) => {
    await chord(s.cue || [60], { duration: 1.8 });
    await new Promise((r) => setTimeout(r, (s.gap_secs ?? 4) * 1000));
    await tone(s.target ?? 60, { duration: 1.6, gain: 0.26 });
  },
  // echo singing: phrase -> silence for the learner's echo -> phrase again to check
  echo_melody: async (s) => {
    await playMidiSequence(s.midi || [], 0, 0.55);
    await new Promise((r) => setTimeout(r, (s.gap_secs ?? 4) * 1000));
    await playMidiSequence(s.midi || [], 0, 0.55);
  },
};

export async function playAudioSpec(spec) {
  if (!spec) return;
  const player = SPEC_PLAYERS[spec.type];
  if (!player) {
    logErr("unknown spec type", spec.type);
    throw new Error("unknown-audio");
  }
  activateIOSSession();
  const pitched = ["interval", "chord", "melody", "melody_vex", "dynamics", "cadence", "cue_note", "echo_melody", "note_rest_pulse", "parts", "dyn_melody"].includes(spec.type);
  const run = async () => {
    const c = await audioCtx(); // created/resumed inside the user gesture
    const played = player(spec);
    played.catch(() => { /* surfaced by the await below */ });
    // Watchdogs only for pitched players that schedule immediately. Percussive
    // tick-based specs (metre, rhythm, tap guides, tempo_compare) are excluded:
    // their ultra-short envelopes false-fail the check on some devices (iOS).
    // assertOutput runs DURING playback: a clock can advance with zero samples
    // reaching the output (macOS device/sample-rate desync).
    if (pitched) {
      await assertAudible(c);
      await assertOutput();
    }
    await played;
  };
  try {
    await run();
  } catch (e) {
    if (!["audio-silent", "audio-stalled", "audio-blocked"].includes(e.message)) throw e;
    // Self-heal once with a brand-new context (still inside the user's gesture).
    logErr("rebuilding audio context after", e.message);
    try { await ctx?.close(); } catch { /* already closed */ }
    ctx = null; bus = null; probe = null;
    await run();
  }
}

export async function playChime(success) {
  const notes = success ? [72, 76, 79] : [63, 58];
  for (let i = 0; i < notes.length; i++) {
    await tone(notes[i], { duration: 0.7, when: i * 0.09, gain: success ? 0.16 : 0.1 });
  }
}
